import React, { useState, useEffect } from 'react';
import ChipsStack from './ChipsStack';
import ReduceBigNumbers from './ReduceBigNumbers';

const Table = (props) => {
    const [moreThenOnePlayerIsAtTheTable, setMoreThenOnePlayerIsAtTheTable] = useState(false);
    const [state, setState] = useState({
        generativeStyle_CardRotate: [],
        generativeStyle_CardMargin: [],
    });

    const [animationClasses, setAnimationClasses] = useState([]);

    const randomInt = (min, max) => Math.random() * (max - min) + min;

    const generateCardsRotation = () => {
        let a = [];
        for (let i = 0; i < 5; i++) {
            a.push('rotate(' + randomInt(-2, 2) + 'deg)');
        }
        setState((prevState) => ({ ...prevState, generativeStyle_CardRotate: a }));
    };

    const generateCardsMargin = () => {
        let m = [];
        for (let i = 0; i < 5; i++) {
            m.push('0 ' + randomInt(0.2, 0.5) + 'rem');
        }
        setState((prevState) => ({ ...prevState, generativeStyle_CardMargin: m }));
    };

    const drawSuiteCard = (suite, highlight) => {
        let drawValue = (value) => {
            return value !== 'T' ? value : '10';
        };

        switch (suite[1]) {
            case 'S':
                return (
                    <div className="black cardSuiteHolder">
                        <span className="cardValue">{drawValue(suite[0])}</span>
                        <span className="cardSuite">{'♠'}</span>
                    </div>
                );
            case 'C':
                return (
                    <div className="black cardSuiteHolder">
                        <span className="cardValue">{drawValue(suite[0])}</span>
                        <span className="cardSuite">{'♣'}</span>
                    </div>
                );
            case 'H':
                return (
                    <div className={highlight === 'muffle' ? 'darkRed cardSuiteHolder' : 'red cardSuiteHolder'}>
                        <span className="cardValue">{drawValue(suite[0])}</span>
                        <span className="cardSuite">{'♥'}</span>
                    </div>
                );
            case 'D':
                return (
                    <div className={highlight === 'muffle' ? 'darkRed cardSuiteHolder' : 'red cardSuiteHolder'}>
                        <span className="cardValue">{drawValue(suite[0])}</span>
                        <span className="cardSuite">{'♦'}</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const drawCards = () => {
        let cards = [];
    
        if (props.roomData?.cards?.length) {
            cards = props.roomData.cards.map((card, i) => {
                let highlight = '';
    
                try {
                    const cardUpper = card.toUpperCase();
                    const winnerParticipant = props.roomData.participants.find(
                        (p) => p.winner?.isWinner && p.winner.evaluatedCards?.length
                    );
                    const winnerCards = winnerParticipant?.winner.evaluatedCards
                        ?.split(',')
                        .map((evalCard) =>
                            evalCard.startsWith('1') ? 'T' + evalCard.slice(2).toUpperCase() : evalCard.toUpperCase()
                        );
    
                    if (winnerCards && winnerCards.includes(cardUpper)) {
                        highlight = 'highlightWinningCards';
                    } else if (
                        !winnerParticipant &&
                        props.privateData.evaluatedCards.cards &&
                        props.privateData?.evaluatedCards?.cards
                            ?.split(',')
                            .map((evalCard) =>
                                evalCard.startsWith('1') ? 'T' + evalCard.slice(2).toUpperCase() : evalCard.toUpperCase()
                            )
                            .includes(cardUpper)
                    ) {
                        highlight = 'highlight';
                    } else {
                        highlight = 'muffle';
                    }
                } catch (e) {
                    console.log(e);
                }
    
                // Combine highlight/muffle class with animation class
                const combinedClasses = `tableCard cardInRoom ${highlight} ${animationClasses[i] || 'invisibleCard'}`;
    
                return (
                    <div
                        key={i}
                        className={combinedClasses} // Use the combined classes
                        style={{
                            transform: state.generativeStyle_CardRotate[i],
                            margin: state.generativeStyle_CardMargin[i],
                        }}
                    >
                        {drawSuiteCard(card, highlight)}
                    </div>
                );
            });
        }
    
        return <div className="tableCardHolder">{cards}</div>;
    };

    useEffect(() => {
        if (state.generativeStyle_CardRotate.length === 0 && state.generativeStyle_CardMargin.length === 0) {
            generateCardsRotation();
            generateCardsMargin();
        }
    
        // Initialize animation classes with empty strings for new cards
        if (props.roomData?.cards?.length) {
            setAnimationClasses((prev) => {
                const updatedClasses = [...prev];
    
                // Ensure the array has the same length as the number of cards
                for (let i = 0; i < props.roomData.cards.length; i++) {
                    if (!updatedClasses[i]) {
                        updatedClasses[i] = 'invisibleCard'; // Initialize new cards with an empty class
                    }
                }
    
                return updatedClasses;
            });
    
            // Add animation classes with unique delays for the first three cards
            props.roomData.cards.forEach((_, i) => {
                let delay;
                if (i === 0) {
                    delay = 100; // First card delay
                } else if (i === 1) {
                    delay = 250; // Second card delay
                } else if (i === 2) {
                    delay = 500; // Third card delay
                } else if (i === 3) {
                    delay = 100; // Third card delay
                } else if (i === 4) {
                    delay = 100; // Third card delay
                }
    
                setTimeout(() => {
                    setAnimationClasses((prev) => {
                        // Check if the class for this card already exists
                        if (prev[i] === 'drawCard') {
                            return prev; // Do not update if the class already exists
                        }
    
                        const updatedClasses = [...prev];
                        updatedClasses[i] = 'drawCard'; // Add the draw animation class
                        return updatedClasses;
                    });
                }, delay); // Apply the unique delay
            });
        }else{
            setAnimationClasses([])
        }
    }, [props.roomData]);

    useEffect(() => {
        checkIfMoreThenOnePlayerIsAtTheTable();
    }, [props.roomData]);

    function checkIfMoreThenOnePlayerIsAtTheTable() {
        let count = 0;

        if (props.roomData && props.roomData.participants && props.roomData.participants.constructor === Array) {
            props.roomData.participants.map((participant) => {
                if (participant.participant && (participant.status === 'player' || participant.status === 'left')) {
                    count += 1;
                }
            });
            if (count > 1) {
                setMoreThenOnePlayerIsAtTheTable(true);
            } else {
                setMoreThenOnePlayerIsAtTheTable(false);
            }
        }
    }

    if (moreThenOnePlayerIsAtTheTable) {
        return (
            <div className="tableWrapper">
                <div className="tableWrapper_data">
                    <div className="bankAndEvaluation">
                        {props.roomData.bankSum ? (
                            <div className="bankSum">
                                <h3>{'$ ' + ReduceBigNumbers(props.roomData.bankSum)}</h3>
                            </div>
                        ) : null}
                        <ChipsStack
                            buyIn={props.roomData.bankSum}
                            currentBet={0}
                            smallBlind={props.roomData?.gameConfig?.smallBlind}
                            className="table_chipsContainer"
                            showBetChips={false}
                        />
                    </div>
                    <div className="cards">{drawCards()}</div>
                    <div className="cardsPlaces">
                        <div className="cardPlace"></div>
                        <div className="cardPlace"></div>
                        <div className="cardPlace"></div>
                        <div className="cardPlace"></div>
                        <div className="cardPlace"></div>
                    </div>
                </div>
            </div>
        );
    }
};

export default Table;